import React, { useEffect, useRef, useState } from "react";

const SCROLL_ROOT_SELECTOR = ".vip-sim-pool-scroll, .vip-sim-wmod-pool-grid, .vip-sim-pool-grid";

function normalizeCandidates(src, fallbacks) {
    const list = [src, ...(Array.isArray(fallbacks) ? fallbacks : [])].filter(Boolean);
    return [...new Set(list)];
}

/**
 * Carrega o ícone quando o cartão entra no scroll visível (contentor ou viewport).
 * Em erro tenta URLs alternativas (ex. itens-de-armas, webp em vez de avif).
 */
export default function LazyCatalogImage({ src, fallbacks, alt = "", className, eager = false }) {
    const wrapRef = useRef(null);
    const candidates = normalizeCandidates(src, fallbacks);
    const [candidateIndex, setCandidateIndex] = useState(0);
    const [shouldLoad, setShouldLoad] = useState(eager);
    const [failed, setFailed] = useState(false);

    const activeSrc = candidates[candidateIndex] ?? null;

    useEffect(() => {
        setCandidateIndex(0);
        setShouldLoad(false);
        setFailed(false);
    }, [src, fallbacks]);

    useEffect(() => {
        if (eager) {
            setShouldLoad(true);
            return undefined;
        }

        const el = wrapRef.current;
        if (!el || !candidates.length) return undefined;

        if (typeof IntersectionObserver === "undefined") {
            setShouldLoad(true);
            return undefined;
        }

        const scrollRoot = el.closest(SCROLL_ROOT_SELECTOR);

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                setShouldLoad(true);
                observer.disconnect();
            },
            {
                root: scrollRoot,
                rootMargin: scrollRoot ? "120px 0px" : "280px 0px",
                threshold: 0.01,
            },
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [eager, candidates.length, src, fallbacks]);

    const handleError = () => {
        if (candidateIndex < candidates.length - 1) {
            setCandidateIndex((i) => i + 1);
            return;
        }
        setFailed(true);
    };

    return (
        <div ref={wrapRef} className="vip-sim-lazy-img">
            {shouldLoad && activeSrc && !failed ? (
                <img
                    className={className}
                    src={activeSrc}
                    alt={alt}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                    draggable={false}
                    onError={handleError}
                />
            ) : failed ? (
                <span className="vip-sim-lazy-img__fallback" aria-hidden>
                    ?
                </span>
            ) : (
                <span className="vip-sim-lazy-img__skeleton" aria-hidden />
            )}
        </div>
    );
}
